import * as Sentry from '@sentry/react';

let sentryInitialized = false;

export function stripUrlSecrets(value) {
  return typeof value === 'string' ? value.split('?')[0].split('#')[0] : value;
}

export function initSentry() {
  // Avoid double initialization
  if (sentryInitialized) return;

  const dsn = process.env.REACT_APP_SENTRY_DSN;

  // Only initialize if DSN is provided
  if (!dsn) {
    console.log('[Sentry] DSN not configured, skipping initialization');
    return;
  }

  const environment = process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV || 'development';
  const enabled = process.env.NODE_ENV === 'production' || process.env.REACT_APP_SENTRY_ENABLED === 'true';

  Sentry.init({
    dsn,
    environment,
    release: process.env.REACT_APP_SENTRY_RELEASE || process.env.REACT_APP_VERSION || undefined,
    enabled,

    // Performance Monitoring
    tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 0.1), // 10% of transactions

    // Session Replay is disabled by default for private studio content.
    replaysSessionSampleRate: Number(process.env.REACT_APP_SENTRY_REPLAY_SESSION_RATE || 0),
    replaysOnErrorSampleRate: Number(process.env.REACT_APP_SENTRY_REPLAY_ERROR_RATE || 0),

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true
      })
    ],

    // Better error grouping
    beforeSend(event, hint) {
      // Don't send errors in development unless explicitly enabled
      if (!enabled) return null;

      // Add custom fingerprinting
      const error = hint.originalException;
      if (error && error.name) {
        event.fingerprint = ['{{ default }}', error.name];
      }

      // Filter out common non-actionable errors
      if (event.exception?.values?.[0]?.value) {
        const errorMessage = event.exception.values[0].value;

        // Filter out ResizeObserver errors (common browser quirk)
        if (errorMessage.includes('ResizeObserver loop')) {
          return null;
        }

        // Filter out Chrome extension errors
        if (errorMessage.includes('chrome-extension://')) {
          return null;
        }
      }

      if (event.user) {
        event.user = event.user.id ? { id: String(event.user.id) } : undefined;
      }
      if (event.request) {
        event.request = {
          method: event.request.method,
          url: stripUrlSecrets(event.request.url)
        };
      }
      return event;
    },

    beforeSendTransaction(event) {
      if (event.request) {
        event.request = { method: event.request.method, url: stripUrlSecrets(event.request.url) };
      }
      if (Array.isArray(event.spans)) {
        event.spans = event.spans.map((span) => ({
          ...span,
          description: stripUrlSecrets(span.description),
          data: undefined
        }));
      }
      return event;
    },

    // Enrich events with user context
    beforeBreadcrumb(breadcrumb) {
      // Don't capture console breadcrumbs in production (too noisy)
      if (breadcrumb.category === 'console' && environment === 'production') {
        return null;
      }
      if (breadcrumb.data) {
        breadcrumb.data = {
          ...(breadcrumb.data.url ? { url: stripUrlSecrets(breadcrumb.data.url) } : {}),
          ...(breadcrumb.data.navigationType ? { navigationType: breadcrumb.data.navigationType } : {})
        };
      }
      return breadcrumb;
    }
  });

  sentryInitialized = true;
  console.log('[Sentry] Initialized successfully', { environment, enabled });
}

// Helper to set user context
export function setSentryUser(user) {
  if (!sentryInitialized) return;

  Sentry.setUser(buildSentryUser(user));
}

export function buildSentryUser(user) {
  return user?.id ? { id: String(user.id) } : null;
}

// Helper to add breadcrumbs
export function addBreadcrumb(message, category = 'action', data = {}) {
  if (!sentryInitialized) return;

  Sentry.addBreadcrumb({
    message,
    category,
    level: 'info',
    data
  });
}

// Helper to capture exceptions manually
export function captureException(error, context = {}) {
  if (!sentryInitialized) {
    console.error('[Sentry] Not initialized, logging locally:', error, context);
    return;
  }

  Sentry.withScope((scope) => {
    const safeContext = {
      source: context.source,
      route: stripUrlSecrets(context.route),
      sessionId: context.sessionId,
      userId: context.userId,
      filename: stripUrlSecrets(context.filename),
      lineno: context.lineno,
      colno: context.colno,
      componentStack: context.componentStack
    };
    Object.entries(safeContext).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (typeof value === 'object') {
        scope.setContext(key, value);
      } else {
        scope.setTag(key, String(value));
      }
    });

    Sentry.captureException(error);
  });
}

// Helper to start performance transaction (Sentry v10+ uses automatic instrumentation)
export function startTransaction(name, op = 'custom') {
  if (!sentryInitialized) return null;

  // In Sentry v10+, use startSpan instead of startTransaction
  return Sentry.startSpan({
    name,
    op
  }, () => {
    // Return a span-like object for compatibility
    return { finish: () => {} };
  });
}

export default Sentry;
